import type { Object3D } from 'three';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../model/SketchEntityReferenceDiagnostics';
import {
	restoreProjectionResultFromReferenceRecord,
	type SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import type {
	InvalidateSketchEntityReferenceItemResult,
	InvalidateSketchEntityReferencesInput,
	InvalidateSketchEntityReferencesResult,
	InvalidateSketchEntityReferencesServiceDependencies,
} from '../../../../types/reference/sketch-entity-reference/InvalidateSketchEntityReferencesService';

export type {
	InvalidateSketchEntityReferenceItemResult,
	InvalidateSketchEntityReferenceItemStatus,
	InvalidateSketchEntityReferencesInput,
	InvalidateSketchEntityReferencesResult,
	InvalidateSketchEntityReferencesRuntime,
	InvalidateSketchEntityReferencesServiceDependencies,
} from '../../../../types/reference/sketch-entity-reference/InvalidateSketchEntityReferencesService';

/**
 * 将一批来源明确失效的草图引用转换为 dangling。
 *
 * 该服务只停止 solver binding 并修改 Record 状态；目标实体和 target tag 必须保留，
 * 供后续警告投影、Undo 恢复和来源修复继续定位同一引用。
 */
export class InvalidateSketchEntityReferencesService {
	/**
	 * @param dependencies 当前文档仓库、关系分发器和目标访问能力。
	 */
	public constructor(
		private readonly dependencies: InvalidateSketchEntityReferencesServiceDependencies,
	) {}

	/**
	 * 使指定引用进入 dangling。
	 *
	 * @param input 来源已经明确失效的引用 ID。
	 * @returns 逐条状态和纯数据影响摘要。
	 */
	public async invalidate(
		input: InvalidateSketchEntityReferencesInput,
	): Promise<InvalidateSketchEntityReferencesResult> {
		const referenceIds = Array.from(new Set(input.referenceIds));
		const references = await this.dependencies.repository.loadReferences(referenceIds);
		const referencesById = new Map(references.map((reference) => [reference.referenceId, reference]));
		const items: InvalidateSketchEntityReferenceItemResult[] = [];
		const prepared: Array<{
			reference: SketchEntityReferenceRecord;
			targetEntity: Object3D;
			diagnostic: ExecutionDiagnostic;
		}> = [];

		for (const referenceId of referenceIds) {
			const reference = referencesById.get(referenceId);
			if (!reference) {
				items.push({ referenceId, status: 'not-found', diagnostics: [] });
				continue;
			}

			if (reference.status === 'dangling') {
				items.push({
					referenceId,
					status: 'dangling',
					reference,
					diagnostics: reference.diagnostic ? [reference.diagnostic] : [],
				});
				continue;
			}

			const targetEntity = this.dependencies.runtime.getSketchObject(
				reference.targetSketchId,
				reference.targetEntityId,
			);
			if (!targetEntity) {
				const diagnostic = createExecutionDiagnostic({
					kind: 'target-missing-cleanup',
					level: 'error',
					message: `来源失效时未找到引用目标实体: ${reference.targetSketchId}/${reference.targetEntityId}`,
					referenceId,
					targetEntityId: reference.targetEntityId,
				});
				items.push({ referenceId, status: 'failed', reference, diagnostics: [diagnostic] });
				continue;
			}

			try {
				this.dependencies.relationAttachDispatcher.detach({ reference, targetEntity });
				prepared.push({
					reference,
					targetEntity,
					diagnostic: createExecutionDiagnostic({
						kind: 'source-missing',
						level: 'warning',
						message: `转换实体引用来源已离场，保留目标实体并标记为 dangling: ${reference.referenceId}`,
						referenceId: reference.referenceId,
						targetSketchId: reference.targetSketchId,
						targetEntityId: reference.targetEntityId,
					}),
				});
			} catch (error) {
				const diagnostic = createExecutionDiagnostic({
					kind: 'restore-failed',
					level: 'error',
					message: `来源失效时拆除引用关系失败: ${error instanceof Error ? error.message : String(error)}`,
					referenceId,
					targetEntityId: reference.targetEntityId,
				});
				items.push({ referenceId, status: 'failed', reference, diagnostics: [diagnostic] });
			}
		}

		if (prepared.length > 0) {
			try {
				await this.dependencies.repository.withWriteTransaction(async (tx) => {
					for (const item of prepared) {
						await tx.updateReferenceStatus(
							item.reference.referenceId,
							'dangling',
							item.diagnostic,
						);
					}
				});
				prepared.forEach(({ reference, diagnostic }) => {
					items.push({
						referenceId: reference.referenceId,
						status: 'dangling',
						reference,
						diagnostics: [diagnostic],
					});
				});
			} catch (error) {
				[...prepared].reverse().forEach(({ reference, targetEntity }) => {
					const itemDiagnostics: ExecutionDiagnostic[] = [];
					try {
						const attachResult = this.dependencies.relationAttachDispatcher.attach({
							reference,
							projectionResult: restoreProjectionResultFromReferenceRecord(reference),
							targetEntity,
							runtimeSourceName: reference.sourceDisplayLabel,
						});
						const attachFailed = !attachResult.record
							|| attachResult.diagnostics.some((diagnostic) => diagnostic.level === 'error');
						if (attachFailed) {
							itemDiagnostics.push(createExecutionDiagnostic({
								kind: 'compensation-failed',
								level: 'error',
								message: `来源失效事务失败后恢复引用关系失败: ${reference.referenceId}`,
								referenceId: reference.referenceId,
								targetEntityId: reference.targetEntityId,
							}));
						}
						itemDiagnostics.push(...attachResult.diagnostics);
					} catch (compensationError) {
						itemDiagnostics.push(createExecutionDiagnostic({
							kind: 'compensation-failed',
							level: 'error',
							message: `来源失效事务失败后恢复引用关系异常: ${compensationError instanceof Error ? compensationError.message : String(compensationError)}`,
							referenceId: reference.referenceId,
							targetEntityId: reference.targetEntityId,
						}));
					}
					const diagnostic = createExecutionDiagnostic({
						kind: 'restore-failed',
						level: 'error',
						message: `来源失效状态提交失败: ${error instanceof Error ? error.message : String(error)}`,
						referenceId: reference.referenceId,
						targetEntityId: reference.targetEntityId,
					});
					items.push({
						referenceId: reference.referenceId,
						status: 'failed',
						reference,
						diagnostics: [diagnostic, ...itemDiagnostics],
					});
				});
			}
		}

		const orderedItems = referenceIds
			.map((referenceId) => items.find((item) => item.referenceId === referenceId))
			.filter((item): item is InvalidateSketchEntityReferenceItemResult => Boolean(item));
		const danglingReferences = orderedItems
			.filter((item) => item.status === 'dangling' && item.reference)
			.map((item) => item.reference as SketchEntityReferenceRecord);
		const diagnostics: ExecutionDiagnostic[] = orderedItems.flatMap((item) => item.diagnostics);

		return {
			items: orderedItems,
			affectedReferenceIds: danglingReferences.map((reference) => reference.referenceId),
			affectedTargetEntityIds: Array.from(new Set(
				danglingReferences.map((reference) => reference.targetEntityId),
			)),
			affectedTargetSketchIds: Array.from(new Set(
				danglingReferences.map((reference) => reference.targetSketchId),
			)),
			diagnostics,
		};
	}
}
